import { useLoaderData } from "react-router-dom";
import Page from "../../components/layout/Page";
import { useFetch } from "../../helpers/useFetch";
import useTitle from "../../hooks/useTitle";
import useVisiblePolling from "../../hooks/useVisiblePolling";
import RoomComputers from "../../components/pcapp/RoomComputers";
import { ActionIcon, Button, Text } from "@mantine/core";
import {
  IconArrowBack,
  IconClockCancel,
  IconPower,
  IconRadar,
  IconRefresh,
  IconRotate,
  IconShield,
  IconShieldOff,
} from "@tabler/icons-react";
import { useState } from "react";
import classNames from "classnames";
import { modalTitle } from "../../components/modals/Modal";
import { openConfirmModal } from "@mantine/modals";
import { openPcBlockModal } from "../../helpers/modals";
import { toast } from "react-hot-toast";

export function loader() {
  return useFetch("/pcapp/room");
}

function isPcOnline(computer) {
  if (!computer?.last_seen_at) return false;
  return Date.now() - new Date(computer.last_seen_at).getTime() < 6 * 60 * 1000;
}

async function sendToAll(computers, type, payload = null) {
  const body = payload ? { type, payload } : { type };
  await Promise.all(
    computers.filter(isPcOnline).map((c) => useFetch(`/pcapp/room/computer/${c.id}/command`, "POST", body))
  );
}

export default function ComputersOverviewPage() {
  const initialRooms = useLoaderData().data;
  const [rooms, setRooms] = useState(initialRooms);
  const [fetching, setFetching] = useState(false);

  useTitle("Přehled počítačů");

  const allComputers = rooms.flatMap((r) => r.computers ?? []);
  const onlineComputers = allComputers.filter(isPcOnline);

  const refreshRooms = () => {
    setFetching(true);
    useFetch("/pcapp/room").then((res) => {
      if (res?.data) setRooms(res.data);
      setFetching(false);
    });
  };

  // Polling jen na popředí + okamžitý refetch po návratu do aplikace (mobil).
  useVisiblePolling(refreshRooms, 15000);

  const turnOffAll = () => {
    openConfirmModal({
      title: modalTitle(IconPower, "Vypnutí všech počítačů"),
      children: (
        <Text size="sm">
          Opravdu chceš vypnout všechny počítače? Tento krok prováděj pouze na konci programu případně v nouzi.
        </Text>
      ),
      labels: { confirm: "Potvrdit", cancel: "Zrušit" },
      onConfirm: async () => {
        await sendToAll(allComputers, "shutdownStart");
        toast.success("Počítače se vypnou za minutu.");
        refreshRooms();
      },
    });
  };

  const rebootAll = () => {
    openConfirmModal({
      title: modalTitle(IconRotate, "Restart všech počítačů"),
      children: <Text size="sm">Opravdu chceš restartovat všechny počítače? Tento krok prováděj pouze v nouzi.</Text>,
      labels: { confirm: "Potvrdit", cancel: "Zrušit" },
      onConfirm: async () => {
        await sendToAll(allComputers, "rebootStart");
        toast.success("Počítače se restartují za minutu.");
        refreshRooms();
      },
    });
  };

  const blockAll = () =>
    openPcBlockModal(async (durationSeconds) => {
      await sendToAll(allComputers, "block", durationSeconds ? { durationSeconds } : null);
      toast.success("Počítače zablokovány.");
      refreshRooms();
    });

  const unblockAll = async () => {
    await sendToAll(allComputers, "unblock");
    toast.success("Počítače odblokovány.");
    refreshRooms();
  };

  const pingAll = async () => {
    await sendToAll(allComputers, "ping");
    toast.success("Ping na počítače odeslán.");
    refreshRooms();
  };

  const endProgramme = async () => {
    await sendToAll(allComputers, "programmeEnd");
    toast.success("Program ukončen.");
    refreshRooms();
  };

  const allBlocked = onlineComputers.length > 0 && onlineComputers.every((c) => c.status === "blocked");
  const allShuttingDown = onlineComputers.length > 0 && onlineComputers.every((c) => c.status === "shuttingDown");
  const allRebooting = onlineComputers.length > 0 && onlineComputers.every((c) => c.status === "rebooting");

  return (
    <Page title="Přehled počítačů">
      <div className="mb-4 grid gap-4 md:grid-cols-5">
        {allBlocked ? (
          <Button
            variant="filled"
            leftSection={<IconShieldOff stroke={1.5} size={20} />}
            color="red"
            onClick={unblockAll}>
            Odblokovat všechny
          </Button>
        ) : (
          <Button variant="light" leftSection={<IconShield stroke={1.5} size={20} />} onClick={blockAll}>
            Zablokovat všechny
          </Button>
        )}

        {allShuttingDown ? (
          <Button
            variant="filled"
            leftSection={<IconArrowBack stroke={1.5} size={20} />}
            color="yellow"
            onClick={() => sendToAll(allComputers, "shutdownAbort").then(refreshRooms)}>
            Zrušit vypnutí
          </Button>
        ) : (
          <Button variant="light" leftSection={<IconPower stroke={1.5} size={20} />} onClick={turnOffAll}>
            Vypnout všechny
          </Button>
        )}

        {allRebooting ? (
          <Button
            variant="filled"
            leftSection={<IconArrowBack stroke={1.5} size={20} />}
            color="yellow"
            onClick={() => sendToAll(allComputers, "rebootAbort").then(refreshRooms)}>
            Zrušit restart
          </Button>
        ) : (
          <Button variant="light" leftSection={<IconRotate stroke={1.5} size={20} />} onClick={rebootAll}>
            Restartovat všechny
          </Button>
        )}

        <Button variant="light" leftSection={<IconClockCancel stroke={1.5} size={20} />} onClick={endProgramme}>
          Konec programu
        </Button>
        <Button variant="light" leftSection={<IconRadar stroke={1.5} size={20} />} onClick={pingAll}>
          Ping na všechny
        </Button>
      </div>

      <div className="mb-4 flex items-center justify-end gap-1">
        <Text c="dimmed">Automatická obnova probíhá každých 30 sekund.</Text>
        <ActionIcon variant="subtle" color="dimmed" size="md" onClick={refreshRooms}>
          <IconRefresh className={classNames({ "animate-spin duration-200": fetching })} size={18} stroke={1.5} />
        </ActionIcon>
      </div>

      {rooms.map((room) => (
        <RoomComputers key={room.id} room={room} onRefresh={refreshRooms} />
      ))}
    </Page>
  );
}
